import { MappingInfo, TargetField } from '../../../../../interfaces/Mapping';
import { IMPORT_PROCESS_STATE } from '../../../../../shared/constants/ImportPageConstants';
import { Autocomplete } from '@mui/material';
import { SovosDivider, SovosTextField, SovosTheme, SovosTypography } from 'mosaic-react';
import { useEffect, useState } from 'react';

const paperCardStyle = {
	flex: '0 0 220px',
	maxWidth: 220,
	padding: 10,
	margin: '0 5px',
	width: 220,
	borderRadius: 8,
};

const overflowTextStyle = {
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
};

const bodyTextStyle = {
	color: SovosTheme.palette.grey[500],
	height: 15,
	margin: '8px 0',
	padding: '0 4px',
	...overflowTextStyle,
};

const renderBodyText = (textToRender: string): JSX.Element => (
	<SovosTypography variant='body1' style={bodyTextStyle}>
		{textToRender}
	</SovosTypography>
);

interface MappingColumnCardProps {
	onDeleteColumnCard: () => void;
	processStatus: number;
	columnsHeaders: any[];
	targetFields: any[];
	columnMapping: any;
	previewColumnRows: any[];
	onFieldSelection: (value: string) => void;
	onSourceFieldSelection: (value: string) => void;
}

const MappingColumnCard = (props: MappingColumnCardProps) => {
	const {
		onDeleteColumnCard,
		processStatus,
		columnsHeaders,
		targetFields,
		columnMapping,
		previewColumnRows,
		onFieldSelection,
		onSourceFieldSelection,
	} = props;
	const [targetFieldValue, setTargetFieldValue] = useState<string>('');
	const [sourceFieldValue, setSourceFieldValue] = useState<string>('');

	useEffect(() => {
		setTargetFieldValue(targetFields.find(target => target.name === columnMapping.targetField)?.label || '');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [columnMapping.targetField]);

	useEffect(() => {
		if (columnsHeaders) {
			setSourceFieldValue(
				columnsHeaders.find(column => column.sourceField === columnMapping.sourceField)?.sourceField || ''
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sourceFieldValue]);

	const cardBorderColor = (): string =>
		processStatus === IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT
			? cardBorderColorForGlobalTemplateEdit()
			: cardBorderForTemplate();

	const cardBorderColorForGlobalTemplateEdit = (): string =>
		columnMapping.sourceField !== ''
			? columnMapping.sourceField !== null
				? `2px solid ${SovosTheme.palette.success.main}`
				: `1px solid ${SovosTheme.palette.grey[400]}`
			: `1px solid ${SovosTheme.palette.grey[400]}`;

	const cardBorderForTemplate = (): string =>
		columnMapping.targetField !== ''
			? columnMapping.targetField !== null
				? `2px solid ${SovosTheme.palette.success.main}`
				: `1px solid ${SovosTheme.palette.grey[400]}`
			: `1px solid ${SovosTheme.palette.grey[400]}`;

	const renderTargetFieldContent = (): JSX.Element => {
		return (
			<>
				<Autocomplete
					options={targetFields
						.map(target => ({
							...target,
							category: `${target.isRequired ? 'Required Fields' : 'Other Fields'}`,
						}))
						.sort((targetA, targetB) => Number(targetB.isRequired) - Number(targetA.isRequired))}
					getOptionDisabled={option => option.disabled!}
					getOptionLabel={option => `${option.label}`}
					isOptionEqualToValue={(option: TargetField, selectedTarget: any) => option.name === selectedTarget}
					groupBy={(option: TargetField) => `${option.category}`}
					style={{ padding: '10px 0' }}
					renderInput={(params: any) => (
						<SovosTextField
							{...params}
							placeholder='Select matching field'
							variant='outlined'
							inputProps={{
								...params.inputProps,
								autoComplete: 'new-password',
							}}
						/>
					)}
					value={columnMapping.targetField || null}
					onChange={(event, selectedTargetField) => onFieldSelection(selectedTargetField?.name || '')}
					inputValue={targetFieldValue}
					onInputChange={(event, newInputValue) => {
						if (newInputValue !== 'undefined') {
							setTargetFieldValue(newInputValue);
						}
					}}
					onBlur={() =>
						setTargetFieldValue(targetFields.find(target => target.name === columnMapping.targetField)?.label || '')
					}
					autoHighlight
					openOnFocus
					clearOnBlur={false}
					selectOnFocus
				/>
			</>
		);
	};

	const renderContent = (processStatus: number) => {
		switch (processStatus) {
			case IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT:
				return (
					<>
						<Autocomplete
							options={columnsHeaders}
							getOptionDisabled={(option: MappingInfo) => option.disabled!}
							getOptionLabel={(option: MappingInfo) => `${option.sourceField}`}
							isOptionEqualToValue={(option: MappingInfo, selectedField: any) => option.sourceField === selectedField}
							renderInput={(params: any) => (
								<SovosTextField
									{...params}
									placeholder='Select header field'
									variant='outlined'
									inputProps={{
										...params.inputProps,
										autoComplete: 'new-password',
									}}
								/>
							)}
							value={columnMapping.sourceField || null}
							onChange={(event, selectedField) => onSourceFieldSelection(selectedField?.sourceField || '')}
							inputValue={sourceFieldValue}
							onInputChange={(event, newInputValue) => {
								if (newInputValue !== 'undefined') {
									setSourceFieldValue(newInputValue);
								}
							}}
							onBlur={() =>
								setSourceFieldValue(
									columnsHeaders.find(column => column.sourceField === columnMapping.sourceField)?.sourceField || ''
								)
							}
							autoHighlight
							openOnFocus
							clearOnBlur={false}
							selectOnFocus
						/>
						{renderTargetFieldContent()}
						<br />
						<div style={{ padding: '0 15px', margin: '10px 0' }}>
							<SovosTypography
								variant='body1'
								style={{ height: 24, padding: '0 0px', fontWeight: 500, ...overflowTextStyle, cursor: 'pointer' }}
								onClick={onDeleteColumnCard}
							>
								Delete column
							</SovosTypography>
						</div>
					</>
				);
			default:
				return (
					<>
						<SovosTypography
							variant='subtitle2'
							style={{ height: 24, padding: '0 10px', fontWeight: 500, ...overflowTextStyle }}
						>
							{columnMapping.sourceField}
						</SovosTypography>
						{renderTargetFieldContent()}
						<div style={{ padding: '0 15px', margin: '10px 0' }}>
							{renderBodyText(previewColumnRows[0])}
							<SovosDivider />
							{renderBodyText(previewColumnRows[1])}
							<SovosDivider />
							{renderBodyText(previewColumnRows[2])}
						</div>
					</>
				);
		}
	};

	return <div style={{ ...paperCardStyle, border: cardBorderColor() }}>{renderContent(processStatus)}</div>;
};

export default MappingColumnCard;
